import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Flag, Store, Globe, HelpCircle } from 'lucide-react';

const SECTORS = ['mining', 'processing', 'infrastructure', 'industrial', 'services', 'other'] as const;

export default function CompanyProfileSetup() {
  const { t } = useTranslation();
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingId, setExistingId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [sectors, setSectors] = useState<string[]>([]);
  const [spaLinked, setSpaLinked] = useState(false);
  const [spaRoleDescription, setSpaRoleDescription] = useState('');
  const [companyType, setCompanyType] = useState('');

  useEffect(() => {
    if (!authLoading && (!user || role !== 'company')) {
      navigate('/auth');
      return;
    }

    if (user) {
      fetchExistingProfile();
    }
  }, [user, role, authLoading, navigate]);

  const fetchExistingProfile = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setExistingId(data.id);
        setName(data.name);
        setCountry(data.country);
        setWebsite(data.website || '');
        setDescription(data.description || '');
        setSectors((data.sectors as string[]) || []);
        setSpaLinked(data.spa_linked);
        setSpaRoleDescription(data.spa_role_description || '');
        setCompanyType(data.company_type || '');
      }
    } catch (error) {
      // No existing profile
    } finally {
      setLoading(false);
    }
  };

  const toggleSector = (sector: string) => {
    setSectors(prev =>
      prev.includes(sector)
        ? prev.filter(s => s !== sector)
        : [...prev, sector]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);

    try {
      const companyData = {
        user_id: user.id,
        name,
        country,
        website: website || null,
        description: description || null,
        sectors,
        spa_linked: spaLinked,
        spa_role_description: spaLinked ? spaRoleDescription : null,
        company_type: companyType || null,
      };

      if (existingId) {
        const { error } = await supabase
          .from('companies')
          .update(companyData)
          .eq('id', existingId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('companies')
          .insert(companyData);

        if (error) throw error;
      }

      toast({ title: 'Profile saved successfully' });
      navigate('/dashboard/company');
    } catch (error) {
      console.error('Error saving company:', error);
      toast({ title: 'Error saving profile', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="container flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-2xl py-8">
        <Card>
          <CardHeader>
            <CardTitle>{t('company.profile.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Type */}
              <div className="space-y-2">
                <Label>{t('auth.signup.companyType')} *</Label>
                <RadioGroup
                  value={companyType}
                  onValueChange={setCompanyType}
                  className="grid grid-cols-1 gap-2"
                >
                  {[
                    { value: 'us_company', icon: Flag },
                    { value: 'congolese_sme', icon: Store },
                    { value: 'multinational', icon: Globe },
                    { value: 'other', icon: HelpCircle },
                  ].map(({ value, icon: Icon }) => (
                    <Label
                      key={value}
                      htmlFor={`setup-ctype-${value}`}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 transition-colors ${
                        companyType === value ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                    >
                      <RadioGroupItem value={value} id={`setup-ctype-${value}`} className="sr-only" />
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div>
                        <span className="text-sm font-medium">{t(`companyProfile.types.${value}`)}</span>
                        <p className="text-xs text-muted-foreground">{t(`companyProfile.types.${value}_desc`)}</p>
                      </div>
                    </Label>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">{t('company.profile.name')} *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">{t('company.profile.country')} *</Label>
                <Select value={country} onValueChange={setCountry} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select country..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="CD">DR Congo</SelectItem>
                    <SelectItem value="ZA">South Africa</SelectItem>
                    <SelectItem value="AU">Australia</SelectItem>
                    <SelectItem value="CA">Canada</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="CH">Switzerland</SelectItem>
                    <SelectItem value="CN">China</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">{t('company.profile.website')}</Label>
                <Input
                  id="website"
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('company.profile.description')}</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="space-y-3">
                <Label>{t('company.profile.sectors')}</Label>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {SECTORS.map(sector => (
                    <Label
                      key={sector}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 transition-colors ${
                        sectors.includes(sector) ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                    >
                      <Checkbox
                        checked={sectors.includes(sector)}
                        onCheckedChange={() => toggleSector(sector)}
                      />
                      <span className="text-sm">{t(`sectors.${sector}`)}</span>
                    </Label>
                  ))}
                </div>
              </div>

              <div className="space-y-4 rounded-lg border p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="spaLinked"
                    checked={spaLinked}
                    onCheckedChange={(checked) => setSpaLinked(checked as boolean)}
                  />
                  <Label htmlFor="spaLinked" className="cursor-pointer leading-tight">
                    {t('company.profile.spaLinked')}
                  </Label>
                </div>

                {spaLinked && (
                  <div className="space-y-2">
                    <Label htmlFor="spaRole">{t('company.profile.spaRole')}</Label>
                    <Textarea
                      id="spaRole"
                      value={spaRoleDescription}
                      onChange={(e) => setSpaRoleDescription(e.target.value)}
                      rows={3}
                      placeholder="Describe your involvement in SPA projects..."
                    />
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('company.profile.save')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}